import type { ResourceAmounts } from '../buildings/definitions';
import type { InfectedConfig } from '../infected/InfectedDirector';

/**
 * Game modes as configuration of ONE shared simulation (Phase 9, GAME_MODES.md,
 * DECISIONS D82–D86). A mode never adds a second rule engine: it picks the
 * start, the infected schedule and the victory rule.
 */
export type ModeId = 'sandbox' | 'classic' | 'last_colony' | 'raid';

export type VictoryRule =
  /** The last team with a Command Center standing wins; your CC falls → you are out. */
  | { readonly kind: 'last_command_center' }
  /** Raid: the attacker wins by destroying the defender's Command Center before the timer; otherwise the defence holds. */
  | { readonly kind: 'raid'; readonly seconds: number };

export interface ModeConfig {
  readonly id: ModeId;
  readonly name: string;
  readonly tagline: string;
  readonly players: { readonly min: number; readonly max: number };
  readonly teams: 'ffa' | '2v2';
  readonly startingResources?: ResourceAmounts;
  readonly startingSurvivors?: number;
  readonly infected: InfectedConfig;
  readonly victory: VictoryRule;
}

export const MODES: Readonly<Record<ModeId, ModeConfig>> = {
  sandbox: {
    id: 'sandbox',
    name: 'Sandbox',
    tagline: 'Build, fight the infected, take on a silent rival colony.',
    players: { min: 1, max: 4 },
    teams: 'ffa',
    infected: { initial: 16, waves: true },
    victory: { kind: 'last_command_center' },
  },
  classic: {
    id: 'classic',
    name: 'Classic RTS',
    tagline: 'Build. Expand. Command. Conquer.',
    players: { min: 2, max: 4 },
    teams: 'ffa',
    // Environmental infected: a pack in the middle, no hordes — they pressure greedy expansion, never decide the match.
    infected: { initial: 24, waves: false },
    victory: { kind: 'last_command_center' },
  },
  last_colony: {
    id: 'last_colony',
    name: 'Last Colony',
    tagline: 'Survive. Expand. Eliminate. Become the last colony.',
    players: { min: 1, max: 4 },
    teams: 'ffa',
    startingResources: { wood: 200, scrap: 90, food: 120 },
    // Escalating, announced pressure: first horde at 3:00, then every 1:30, +3 each time, up to 45.
    infected: { initial: 20, waves: true, firstWaveSeconds: 180, waveEverySeconds: 90, waveSize: 8, waveGrowth: 3, maxWaveSize: 45 },
    victory: { kind: 'last_command_center' },
  },
  raid: {
    id: 'raid',
    name: 'Raid & Domination',
    tagline: 'Build your base. Attack your rivals. Climb the rankings.',
    players: { min: 2, max: 2 },
    teams: 'ffa',
    startingResources: { wood: 0, scrap: 0, food: 50 },
    infected: { initial: 0, waves: false },
    victory: { kind: 'raid', seconds: 8 * 60 },
  },
};

export function isModeId(v: unknown): v is ModeId {
  return typeof v === 'string' && v in MODES;
}
